import { IntegrationType, TemplateType } from '@shared/generated/graphql';
import { ConfigOverrides, IntegrationConfigs } from '@shared/types/order';
import { createContext, useContext } from 'react';

export enum VoomaBuildFeature {
  MULTIPLE_COMMODITIES = 'MULTIPLE_COMMODITIES',
}

export type VoomaBuildIntegrationContextType = {
  supports: (feature: VoomaBuildFeature) => boolean;
};

export const VoomaBuildIntegrationContext =
  createContext<VoomaBuildIntegrationContextType>({
    supports: () => false,
  });

export function useVoomaBuildIntegration() {
  const context = useContext(VoomaBuildIntegrationContext);
  if (!context) {
    throw new Error(
      'useVoomaBuildIntegration must be used within a VoomaBuildIntegrationProvider'
    );
  }
  return context;
}

export const VoomaBuildIntegrationProvider = ({
  children,
  organizationIntegrationConfigs,
  originatorConfigOverrides,
}: {
  children: React.ReactNode;
  organizationIntegrationConfigs: IntegrationConfigs;
  originatorConfigOverrides?: ConfigOverrides;
}) => {
  const voomaBuildIntegration = makeVoomaBuildIntegration(
    organizationIntegrationConfigs,
    originatorConfigOverrides
  );
  const supports = (feature: VoomaBuildFeature) => {
    return voomaBuildIntegration.supports(feature);
  };

  return (
    <VoomaBuildIntegrationContext.Provider value={{ supports }}>
      {children}
    </VoomaBuildIntegrationContext.Provider>
  );
};

// TODO(mike): Itemize the support for other features.
class VoomaBuildIntegration {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  supports(_feature: VoomaBuildFeature): boolean {
    return true;
  }
}

class McLeodIntegration extends VoomaBuildIntegration {
  supports(feature: VoomaBuildFeature): boolean {
    switch (feature) {
      case VoomaBuildFeature.MULTIPLE_COMMODITIES:
        return false;
      default:
        return true;
    }
  }
}

function makeVoomaBuildIntegration(
  organizationIntegrationConfigs: IntegrationConfigs,
  originatorConfigOverrides?: ConfigOverrides
) {
  const relevantOrgConfig = organizationIntegrationConfigs.integrations.find(
    (ic) => ic.orderType === TemplateType.FREIGHT_ORDER
  );
  const relevantOriginatorConfig =
    originatorConfigOverrides?.integrations?.find(
      (ic) => ic.orderType === TemplateType.FREIGHT_ORDER
    );
  const relevantConfig = relevantOriginatorConfig || relevantOrgConfig;
  if (!relevantConfig) return new VoomaBuildIntegration();
  switch (relevantConfig?.integrationType) {
    case IntegrationType.MCLEOD:
      return new McLeodIntegration();
    default:
      return new VoomaBuildIntegration();
  }
}
